import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { ContasService, TransacoesService, CategoriasService, AnexosService, fmtMoeda, fmtData, mesAtual } from '../services';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const initEdit = { descricao: '', valor: '', data: '', categoria_id: '', status: 'efetivado', observacoes: '' };

export default function Extrato() {
  const { isViewer } = useAuth();

  const [contas, setContas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [contaId, setContaId] = useState('');
  const [tipo, setTipo] = useState('');
  const [modo, setModo] = useState('mes');
  const [mes, setMes] = useState(mesAtual());
  const [ano, setAno] = useState(new Date().getFullYear());
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [transacoes, setTransacoes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [editando, setEditando] = useState(null);
  const [editForm, setEditForm] = useState(initEdit);
  const [salvando, setSalvando] = useState(false);
  const [erroEdit, setErroEdit] = useState('');

  const [saldoAnterior, setSaldoAnterior] = useState(null);

  const [viewing, setViewing] = useState(null);
  const [viewAnexos, setViewAnexos] = useState([]);

  const navegarMes = (delta) => {
    const [y, m] = mes.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMes(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const nomeMesAtual = () => `${MESES[parseInt(mes.substring(5, 7)) - 1]} ${mes.substring(0, 4)}`;

  useEffect(() => {
    ContasService.listar().then(setContas).catch(() => {});
    if (!isViewer) CategoriasService.listar().then(setCategorias).catch(() => {});
  }, [isViewer]);

  const carregarTransacoes = () => {
    if (modo === 'periodo' && (!inicio || !fim)) return;
    let cancelled = false;
    setLoading(true);
    const params = { limit: 500 };
    if (contaId) params.conta_id = contaId;
    if (tipo) params.tipo = tipo;
    if (modo === 'mes') params.mes = mes;
    else if (modo === 'ano') params.ano = ano;
    else if (modo === 'periodo') { params.inicio = inicio; params.fim = fim; }
    TransacoesService.listar(params)
      .then(d => { if (!cancelled) { setTransacoes(d.transacoes || []); setTotal(d.total || 0); } })
      .catch(() => { if (!cancelled) { setTransacoes([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  };

  useEffect(carregarTransacoes, [contaId, tipo, modo, mes, ano, inicio, fim]);

  useEffect(() => {
    let antes_de;
    if (modo === 'mes') antes_de = mes + '-01';
    else if (modo === 'ano') antes_de = `${ano}-01-01`;
    else if (modo === 'periodo' && inicio) antes_de = inicio;
    else { setSaldoAnterior(null); return; }
    const params = { antes_de };
    if (contaId) params.conta_id = contaId;
    TransacoesService.saldoAnterior(params)
      .then(d => setSaldoAnterior(d.saldo ?? 0))
      .catch(() => setSaldoAnterior(null));
  }, [modo, mes, ano, inicio, contaId]);

  const openView = (t) => {
    setViewing(t);
    setViewAnexos([]);
    AnexosService.listar(t.id).then(setViewAnexos).catch(() => setViewAnexos([]));
  };

  const fmtTamanho = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const abrirEdicao = (t) => {
    setEditando(t);
    setEditForm({
      descricao: t.descricao,
      valor: t.valor,
      data: String(t.data).substring(0, 10),
      categoria_id: t.categoria_id || '',
      status: t.status || 'efetivado',
      observacoes: t.observacoes || '',
    });
    setErroEdit('');
  };

  const salvarEdicao = async (e) => {
    e.preventDefault();
    setSalvando(true); setErroEdit('');
    try {
      await TransacoesService.atualizar(editando.id, {
        tipo: editando.tipo,
        descricao: editForm.descricao,
        valor: editForm.valor,
        data: editForm.data,
        categoria_id: editForm.categoria_id || null,
        conta_id: editando.conta_id,
        status: editForm.status,
        observacoes: editForm.observacoes || null,
      });
      setEditando(null);
      carregarTransacoes();
    } catch (err) {
      setErroEdit(err.response?.data?.erro || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const totalReceitas = transacoes.filter(t => t.tipo === 'receita' && t.status === 'efetivado').reduce((s, t) => s + parseFloat(t.valor), 0);
  const totalDespesas = transacoes.filter(t => t.tipo === 'despesa' && t.status === 'efetivado').reduce((s, t) => s + parseFloat(t.valor), 0);
  const pendReceitas  = transacoes.filter(t => t.tipo === 'receita' && t.status !== 'efetivado').reduce((s, t) => s + parseFloat(t.valor), 0);
  const pendDespesas  = transacoes.filter(t => t.tipo === 'despesa' && t.status !== 'efetivado').reduce((s, t) => s + parseFloat(t.valor), 0);
  const saldo = totalReceitas - totalDespesas;
  const saldoFinal = (saldoAnterior ?? 0) + saldo;

  const exportarPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const contaNome = contaId ? (contas.find(c => String(c.id) === String(contaId))?.nome || 'Conta selecionada') : 'Todas as contas';

    let periodoLabel = '';
    if (modo === 'mes') periodoLabel = `Mês: ${nomeMesAtual()}`;
    else if (modo === 'ano') periodoLabel = `Ano: ${ano}`;
    else if (modo === 'periodo') periodoLabel = `Período: ${inicio} a ${fim}`;

    const tipoLabel = tipo === 'receita' ? 'Receitas' : tipo === 'despesa' ? 'Despesas' : 'Todas';

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(14, 22, 196, 22);
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text('Controle Financeiro Pessoal', 14, 10);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('Extrato de Transações', 14, 17);

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(8.5);
    let y = 30;
    doc.text(`Conta: ${contaNome}`, 14, y);
    doc.text(`Tipo: ${tipoLabel}`, 80, y);
    doc.text(periodoLabel, 140, y);
    y += 5;
    doc.setTextColor(130, 130, 130);
    doc.setFontSize(7.5);
    doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`, 14, y);
    y += 5;

    doc.setDrawColor(210, 210, 210);
    doc.line(14, y, 196, y);
    y += 6;

    const pdfCards = [];
    if (saldoAnterior !== null) {
      pdfCards.push({ label: 'Saldo Anterior', valor: (saldoAnterior >= 0 ? '+' : '') + fmtMoeda(saldoAnterior), cor: saldoAnterior >= 0 ? [37, 99, 180] : [185, 28, 28] });
    }
    pdfCards.push({ label: 'Rec. Efetivadas', valor: '+' + fmtMoeda(totalReceitas), cor: [34, 130, 74] });
    pdfCards.push({ label: 'Desp. Efetivadas', valor: '-' + fmtMoeda(totalDespesas), cor: [185, 28, 28] });
    pdfCards.push({ label: saldoAnterior !== null ? 'Saldo Final' : 'Saldo do Período', valor: (saldoFinal >= 0 ? '+' : '') + fmtMoeda(saldoFinal), cor: saldoFinal >= 0 ? [37, 99, 180] : [185, 28, 28] });
    if (pendReceitas > 0 || pendDespesas > 0) {
      const liquido = pendReceitas - pendDespesas;
      pdfCards.push({ label: 'A Efetivar', valor: (liquido >= 0 ? '+' : '') + fmtMoeda(liquido), cor: [180, 83, 9] });
      const projecao = saldoFinal + pendReceitas - pendDespesas;
      pdfCards.push({ label: 'Projeção Saldo', valor: (projecao >= 0 ? '+' : '') + fmtMoeda(projecao), cor: projecao >= 0 ? [180, 83, 9] : [185, 28, 28] });
    }
    const maxPerRow = 4;
    for (let r = 0; r < pdfCards.length; r += maxPerRow) {
      const row = pdfCards.slice(r, r + maxPerRow);
      const colW = Math.floor((182 - (row.length - 1) * 5) / row.length);
      row.forEach((item, i) => {
        const x = 14 + i * (colW + 5);
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        doc.roundedRect(x, y, colW, 13, 2, 2, 'S');
        doc.setTextColor(100, 116, 139);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(item.label, x + 4, y + 5);
        doc.setTextColor(...item.cor);
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'bold');
        doc.text(item.valor, x + 4, y + 10.5);
      });
      y += 17;
    }
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [['Data', 'Descrição', 'Categoria', 'Conta', 'Status', 'Valor']],
      body: transacoes.map(t => [
        fmtData(t.data),
        t.observacoes ? `${t.descricao}\n${t.observacoes}` : t.descricao,
        t.categoria_nome || '—',
        t.conta_nome || (t.cartao_nome ? `CC: ${t.cartao_nome}` : '—'),
        t.status === 'efetivado' ? 'Efetivado' : 'Pendente',
        `${t.tipo === 'receita' ? '+' : '-'}${fmtMoeda(t.valor)}`,
      ]),
      headStyles: { fillColor: [235, 238, 242], textColor: [30, 41, 59], fontSize: 8, fontStyle: 'bold', halign: 'left', lineColor: [200, 200, 200], lineWidth: 0.2 },
      bodyStyles: { fontSize: 8, textColor: [50, 50, 50], fillColor: [255, 255, 255], lineColor: [220, 220, 220], lineWidth: 0.1 },
      alternateRowStyles: { fillColor: [250, 251, 252] },
      columnStyles: { 0: { cellWidth: 20 }, 1: { cellWidth: 52 }, 2: { cellWidth: 35 }, 3: { cellWidth: 35 }, 4: { cellWidth: 22 }, 5: { cellWidth: 26, halign: 'right' } },
      didParseCell: (data) => {
        if (data.column.index === 5 && data.section === 'body') {
          const val = data.cell.raw || '';
          data.cell.styles.textColor = val.startsWith('+') ? [34, 130, 74] : [185, 28, 28];
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.column.index === 4 && data.section === 'body') {
          data.cell.styles.textColor = data.cell.raw === 'Efetivado' ? [34, 130, 74] : [160, 100, 0];
        }
      },
      didDrawCell: (data) => {
        if (data.column.index === 1 && data.section === 'body' && data.row.index < transacoes.length) {
          const t = transacoes[data.row.index];
          if (t?.observacoes) {
            const pad = 2;
            // Calcular quantas linhas a descrição ocupa em 8pt para achar o Y da obs
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            const descLines = doc.splitTextToSize(t.descricao, data.cell.width - pad * 2);
            const lineH = 3.8; // altura aproximada de linha a 8pt em mm
            const obsStartY = data.cell.y + pad + descLines.length * lineH;

            // Cobrir o texto da obs que autotable desenhou no estilo normal
            const fill = data.cell.styles.fillColor;
            doc.setFillColor(
              Array.isArray(fill) ? fill[0] : 255,
              Array.isArray(fill) ? fill[1] : 255,
              Array.isArray(fill) ? fill[2] : 255
            );
            doc.rect(data.cell.x + 0.5, obsStartY - 0.8, data.cell.width - 1, data.cell.y + data.cell.height - obsStartY + 0.8, 'F');

            // Redesenhar obs em estilo discreto
            doc.setFontSize(6.5);
            doc.setTextColor(155, 155, 155);
            doc.setFont('helvetica', 'italic');
            const obsLines = doc.splitTextToSize(t.observacoes, data.cell.width - pad * 2);
            doc.text(obsLines, data.cell.x + pad, obsStartY + 2.2);

            // Restaurar
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(50, 50, 50);
          }
        }
      },
      margin: { left: 14, right: 14 },
      foot: [['', `Total: ${transacoes.length} lançamento${transacoes.length !== 1 ? 's' : ''}`, '', '', '', `Saldo: ${saldo >= 0 ? '+' : '-'}${fmtMoeda(Math.abs(saldo))}`]],
      footStyles: { fillColor: [235, 238, 242], textColor: [30, 41, 59], fontSize: 8, fontStyle: 'bold', lineColor: [200, 200, 200], lineWidth: 0.2 },
    });

    const fileName = `extrato_${contaNome.replace(/\s+/g, '_').toLowerCase()}_${periodoLabel.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
    doc.save(fileName);
  };

  const navBtnStyle = {
    background: 'none', border: 'none', padding: '8px 14px',
    cursor: 'pointer', color: 'var(--gray-500)', fontSize: 18, lineHeight: 1,
    transition: 'color .15s',
  };
  const navLabelStyle = {
    padding: '0 18px', fontSize: 13.5, fontWeight: 700,
    borderLeft: '1px solid var(--gray-200)', borderRight: '1px solid var(--gray-200)',
    textAlign: 'center', color: 'var(--gray-800)',
  };
  const navWrapStyle = {
    display: 'inline-flex', alignItems: 'center',
    border: '1.5px solid var(--gray-200)', borderRadius: 9,
    overflow: 'hidden', background: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,.06)',
  };

  return (
    <Layout title="Extrato">

      {/* ── Filtros ── */}
      <div className="card mb-3">
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Linha 1: Conta + Tipo */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Conta</label>
              <select className="form-control" value={contaId} onChange={e => setContaId(e.target.value)}>
                <option value="">Todas as contas</option>
                {contas.map(c => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Tipo</label>
              <select className="form-control" value={tipo} onChange={e => setTipo(e.target.value)}>
                <option value="">Todos</option>
                <option value="receita">Receitas</option>
                <option value="despesa">Despesas</option>
              </select>
            </div>
          </div>

          {/* Linha 2: Modo + Navegador de período */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>

            {/* Toggle Mês / Ano / Período */}
            <div style={{ display: 'flex', border: '1.5px solid var(--gray-200)', borderRadius: 9, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
              {[['mes', 'Mês'], ['ano', 'Ano'], ['periodo', 'Período']].map(([k, v]) => (
                <button key={k} type="button" onClick={() => setModo(k)} style={{
                  padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: modo === k ? 'var(--primary)' : 'white',
                  color: modo === k ? 'white' : 'var(--gray-600)',
                  transition: 'all .15s',
                }}>{v}</button>
              ))}
            </div>

            {/* Navegador de mês com setas */}
            {modo === 'mes' && (
              <div style={navWrapStyle}>
                <button type="button" style={navBtnStyle} onClick={() => navegarMes(-1)} title="Mês anterior">‹</button>
                <span style={{ ...navLabelStyle, minWidth: 160 }}>{nomeMesAtual()}</span>
                <button type="button" style={navBtnStyle} onClick={() => navegarMes(1)} title="Próximo mês">›</button>
              </div>
            )}

            {/* Navegador de ano com setas */}
            {modo === 'ano' && (
              <div style={navWrapStyle}>
                <button type="button" style={navBtnStyle} onClick={() => setAno(a => parseInt(a) - 1)} title="Ano anterior">‹</button>
                <span style={{ ...navLabelStyle, minWidth: 80 }}>{ano}</span>
                <button type="button" style={navBtnStyle} onClick={() => setAno(a => parseInt(a) + 1)} title="Próximo ano">›</button>
              </div>
            )}

            {/* Período personalizado */}
            {modo === 'periodo' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">De</label>
                  <input type="date" className="form-control" value={inicio} onChange={e => setInicio(e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Até</label>
                  <input type="date" className="form-control" value={fim} onChange={e => setFim(e.target.value)} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Totalizadores ── */}
      <div className="stat-grid" style={{ marginBottom: 16 }}>
        {saldoAnterior !== null && (
          <div className={`stat-card ${saldoAnterior >= 0 ? 'saldo' : 'despesa'}`} style={{ background: 'linear-gradient(135deg,#f8fafc,#f1f5f9)', border: '1px solid #cbd5e1' }}>
            <span className="stat-label" style={{ color: '#64748b' }}>Saldo Anterior</span>
            <span className="stat-value" style={{ color: saldoAnterior >= 0 ? 'var(--primary)' : 'var(--danger)' }}>
              {saldoAnterior >= 0 ? '+' : ''}{fmtMoeda(saldoAnterior)}
            </span>
          </div>
        )}
        <div className="stat-card receita">
          <span className="stat-label">Receitas efetivadas</span>
          <span className="stat-value">{fmtMoeda(totalReceitas)}</span>
        </div>
        <div className="stat-card despesa">
          <span className="stat-label">Despesas efetivadas</span>
          <span className="stat-value">{fmtMoeda(totalDespesas)}</span>
        </div>
        <div className={`stat-card ${saldoFinal >= 0 ? 'saldo' : 'despesa'}`}>
          <span className="stat-label">{saldoAnterior !== null ? 'Saldo Final' : 'Saldo do período'}</span>
          <span className="stat-value">{saldoFinal >= 0 ? '+' : '-'}{fmtMoeda(Math.abs(saldoFinal))}</span>
        </div>
        {(pendReceitas > 0 || pendDespesas > 0) && (() => {
          const liquido = pendReceitas - pendDespesas;
          const positivo = liquido >= 0;
          return (
            <div className="stat-card" style={{ background: 'linear-gradient(135deg,#fffbeb,#fef3c7)', border: '1px solid #fde68a', boxShadow: '0 2px 8px rgba(217,119,6,.15)' }}>
              <span className="stat-label" style={{ color: '#92400e' }}>⏳ A Efetivar</span>
              <span className="stat-value" style={{ color: positivo ? '#b45309' : '#b91c1c' }}>{positivo ? '+' : ''}{fmtMoeda(liquido)}</span>
            </div>
          );
        })()}
        {(pendReceitas > 0 || pendDespesas > 0) && (() => {
          const projecao = saldoFinal + pendReceitas - pendDespesas;
          const positivo = projecao >= 0;
          return (
            <div className="stat-card" style={{ background: 'linear-gradient(135deg,#fffbeb,#fef3c7)', border: '1px solid #fde68a', boxShadow: '0 2px 8px rgba(217,119,6,.15)' }}>
              <span className="stat-label" style={{ color: '#92400e' }}>⏳ Projeção Saldo Final</span>
              <span className="stat-value" style={{ color: positivo ? '#b45309' : '#b91c1c' }}>{positivo ? '+' : '-'}{fmtMoeda(Math.abs(projecao))}</span>
            </div>
          );
        })()}
      </div>

      {/* ── Lista de lançamentos ── */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">📄 Lançamentos</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="text-sm text-muted">{total} registro{total !== 1 ? 's' : ''}</span>
            <button
              onClick={exportarPDF}
              className="btn btn-ghost btn-sm"
              disabled={transacoes.length === 0 || loading}
            >
              📥 PDF
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading-wrap"><div className="spinner" /></div>
        ) : transacoes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📄</div>
            <p>Nenhuma transação encontrada para os filtros selecionados.</p>
          </div>
        ) : (
          <>
            {/* Tabela — visível apenas no desktop */}
            <div className="table-wrap extrato-desktop">
              <table>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Descrição</th>
                    <th>Categoria</th>
                    <th>Conta</th>
                    <th>Status</th>
                    <th>Valor</th>
                    <th style={{ width: isViewer ? 48 : 88 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {transacoes.map(t => (
                    <tr key={t.id}>
                      <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtData(t.data)}</td>
                      <td style={{ fontSize: 13 }}>{t.descricao}</td>
                      <td style={{ fontSize: 13 }}>
                        {t.categoria_icone && <span style={{ marginRight: 4 }}>{t.categoria_icone}</span>}
                        {t.categoria_nome || <span className="text-muted">—</span>}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {t.conta_nome ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span className="color-dot" style={{ background: t.conta_cor }} />
                            {t.conta_nome}
                          </span>
                        ) : t.cartao_nome ? (
                          <span>💳 {t.cartao_nome}</span>
                        ) : <span className="text-muted">—</span>}
                      </td>
                      <td>
                        <span className={`badge ${t.status === 'efetivado' ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: 10 }}>
                          {t.status === 'efetivado' ? 'Efetivado' : 'Pendente'}
                        </span>
                      </td>
                      <td className={t.tipo === 'receita' ? 'value-positive' : 'value-negative'} style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {t.tipo === 'receita' ? '+' : '-'}{fmtMoeda(t.valor)}
                      </td>
                      <td>
                        <div className="flex-gap gap-sm">
                          <button className="btn-action btn-action-view" title="Visualizar" onClick={() => openView(t)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M2 12s3.636-7 10-7 10 7 10 7-3.636 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                          {!isViewer && (
                            <button className="btn-action btn-action-edit" title="Editar" onClick={() => abrirEdicao(t)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards — visíveis apenas no mobile */}
            <div className="extrato-mobile">
              {transacoes.map(t => (
                <div key={t.id} className="extrato-item">
                  <div className="extrato-item-top">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="extrato-item-desc">{t.descricao}</div>
                      <div className="extrato-item-meta">
                        {t.categoria_icone && <span>{t.categoria_icone}</span>}
                        <span>{t.categoria_nome || '—'}</span>
                        <span style={{ color: 'var(--gray-300)' }}>·</span>
                        <span>{fmtData(t.data)}</span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div className={`extrato-item-valor ${t.tipo === 'receita' ? 'value-positive' : 'value-negative'}`}>
                        {t.tipo === 'receita' ? '+' : '-'}{fmtMoeda(t.valor)}
                      </div>
                      <span className={`badge ${t.status === 'efetivado' ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: 10 }}>
                        {t.status === 'efetivado' ? 'Efetivado' : 'Pendente'}
                      </span>
                    </div>
                  </div>
                  <div className="extrato-item-bottom">
                    <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                      {t.conta_nome ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <span className="color-dot" style={{ background: t.conta_cor }} />
                          {t.conta_nome}
                        </span>
                      ) : t.cartao_nome ? `💳 ${t.cartao_nome}` : '—'}
                    </span>
                    <div className="flex-gap gap-sm">
                      <button className="btn-action btn-action-view" title="Visualizar" onClick={() => openView(t)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 12s3.636-7 10-7 10 7 10 7-3.636 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>
                        </svg>
                      </button>
                      {!isViewer && (
                        <button className="btn-action btn-action-edit" title="Editar" onClick={() => abrirEdicao(t)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Modal de edição (admin only) ── */}
      {editando && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditando(null)}>
          <div className="modal modal-sm" style={{ overflow: 'hidden' }}>

            {/* Header dinâmico */}
            <div style={{
              padding: '16px 22px',
              background: editando.tipo === 'receita'
                ? 'linear-gradient(135deg,#f0fdf4,#dcfce7)'
                : 'linear-gradient(135deg,#fff5f5,#fee2e2)',
              borderBottom: `2px solid ${editando.tipo === 'receita' ? '#86efac' : '#fca5a5'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.09em', color: 'var(--gray-400)', marginBottom: 2 }}>
                  Editar Transação
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: editando.tipo === 'receita' ? 'var(--success)' : 'var(--danger)' }}>
                  {editando.tipo === 'receita' ? '⬆️ Receita' : '⬇️ Despesa'}
                </div>
              </div>
              <button className="modal-close" onClick={() => setEditando(null)}>×</button>
            </div>

            <form onSubmit={salvarEdicao}>
              <div className="modal-body" style={{ background: '#f7f9fc', gap: 14 }}>
                {erroEdit && <div className="alert alert-danger">{erroEdit}</div>}

                <div className="form-group">
                  <label className="form-label">Descrição</label>
                  <input className="form-control" required value={editForm.descricao}
                    onChange={e => setEditForm(f => ({ ...f, descricao: e.target.value }))} />
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Valor (R$)</label>
                    <input className="form-control" type="number" step="0.01" min="0.01" required
                      value={editForm.valor} onChange={e => setEditForm(f => ({ ...f, valor: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Data</label>
                    <input className="form-control" type="date" required
                      value={editForm.data} onChange={e => setEditForm(f => ({ ...f, data: e.target.value }))} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Categoria</label>
                  <select className="form-control" value={editForm.categoria_id}
                    onChange={e => setEditForm(f => ({ ...f, categoria_id: e.target.value }))}>
                    <option value="">Sem categoria</option>
                    {categorias.filter(c => c.tipo === editando.tipo || c.tipo === 'ambos').map(c => (
                      <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-control" value={editForm.status}
                    onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="efetivado">Efetivado</option>
                    <option value="pendente">Pendente</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Observações</label>
                  <input className="form-control" placeholder="Opcional"
                    value={editForm.observacoes} onChange={e => setEditForm(f => ({ ...f, observacoes: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer" style={{ background: '#f7f9fc', borderTop: '1px solid var(--gray-200)' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setEditando(null)}>Cancelar</button>
                <button type="submit" disabled={salvando} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '9px 22px', borderRadius: 9, border: 'none', cursor: 'pointer',
                  fontWeight: 700, fontSize: 14, color: 'white', transition: 'all .2s',
                  background: editando.tipo === 'receita'
                    ? 'linear-gradient(135deg,var(--success),#15803d)'
                    : 'linear-gradient(135deg,var(--danger),#b91c1c)',
                  boxShadow: editando.tipo === 'receita'
                    ? '0 4px 14px rgba(22,163,74,.35)'
                    : '0 4px 14px rgba(220,38,38,.35)',
                  opacity: salvando ? 0.7 : 1,
                }}>
                  {salvando ? 'Salvando...' : '✓ Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ── Modal de visualização ── */}
      {viewing && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewing(null)}>
          <div className="modal modal-md" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

            {/* Hero */}
            <div style={{
              padding: '16px 20px 24px',
              background: viewing.tipo === 'receita'
                ? 'linear-gradient(135deg,#16a34a,#22c55e)'
                : 'linear-gradient(135deg,#dc2626,#f87171)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,.75)', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>
                    {viewing.tipo === 'receita' ? 'Receita' : 'Despesa'}
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: 'white', lineHeight: 1.1, letterSpacing: '-.02em' }}>
                    {viewing.tipo === 'receita' ? '+' : '-'}{fmtMoeda(viewing.valor)}
                  </div>
                  <div style={{ fontSize: 14, color: 'rgba(255,255,255,.9)', marginTop: 8, fontWeight: 500 }}>
                    {viewing.descricao}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, flexShrink: 0 }}>
                  <button onClick={() => setViewing(null)} style={{
                    background: 'rgba(255,255,255,.2)', border: 'none', borderRadius: '50%',
                    width: 30, height: 30, cursor: 'pointer', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                  }}>×</button>
                  <span style={{
                    background: 'rgba(255,255,255,.25)', backdropFilter: 'blur(4px)',
                    color: 'white', padding: '5px 14px', borderRadius: 100,
                    fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                  }}>
                    {viewing.status === 'efetivado' ? '✓ Efetivado' : '⏳ Pendente'}
                  </span>
                </div>
              </div>
            </div>

            {/* Corpo */}
            <div style={{ background: '#f7f9fc', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', flex: 1 }}>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { label: 'Data', value: fmtData(viewing.data) },
                  { label: 'Categoria', value: `${viewing.categoria_icone || ''} ${viewing.categoria_nome || '—'}`.trim() },
                  { label: viewing.cartao_id ? 'Cartão' : 'Conta', value: viewing.conta_nome || viewing.cartao_nome || '—' },
                  viewing.observacoes ? { label: 'Observações', value: viewing.observacoes, full: true } : null,
                ].filter(Boolean).map(({ label, value, full }) => (
                  <div key={label} style={{
                    background: 'white', borderRadius: 10, padding: '10px 14px',
                    border: '1px solid var(--gray-100)', boxShadow: '0 1px 4px rgba(0,0,0,.05)',
                    gridColumn: full ? '1/-1' : undefined,
                  }}>
                    <div style={{ fontSize: 10, color: 'var(--gray-400)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-800)' }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Anexos */}
              <div>
                <div style={{ fontSize: 10, color: 'var(--gray-400)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  Anexos
                </div>
                {viewAnexos.length === 0 ? (
                  <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>Nenhum anexo.</p>
                ) : viewAnexos.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'white', borderRadius: 8, marginBottom: 6, border: '1px solid var(--gray-200)', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--gray-700)', fontWeight: 500 }}>{a.nome_original}</span>
                    {a.tamanho && <span style={{ fontSize: 11, color: 'var(--gray-400)', flexShrink: 0 }}>{fmtTamanho(a.tamanho)}</span>}
                    <a href={AnexosService.urlDownload(a.id)} target="_blank" rel="noreferrer" className="btn-action btn-action-view" style={{ width: 28, height: 28 }} title="Baixar">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </a>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer" style={{ background: '#f7f9fc', borderTop: '1px solid var(--gray-200)' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setViewing(null)}>Fechar</button>
              {!isViewer && (
                <button type="button"
                  onClick={() => { setViewing(null); abrirEdicao(viewing); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '9px 22px', borderRadius: 9, border: 'none', cursor: 'pointer',
                    fontWeight: 700, fontSize: 14, color: 'white', transition: 'all .2s',
                    background: viewing.tipo === 'receita'
                      ? 'linear-gradient(135deg,var(--success),#15803d)'
                      : 'linear-gradient(135deg,var(--danger),#b91c1c)',
                    boxShadow: viewing.tipo === 'receita'
                      ? '0 4px 14px rgba(22,163,74,.35)'
                      : '0 4px 14px rgba(220,38,38,.35)',
                  }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                  Editar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
